import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import z from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useApiMutation } from '@/modules/common';
import { EventProductResponseItem } from '@/modules/events/types';
import { EventProduct } from '@/modules/eventsV2/types';

import { editInventoryAmountEventSchema } from '../schema';

interface InventoryDetailsProps {
  isOpen: boolean;
  selectedInventory: EventProduct;
  eventId: string;
  stationId: string;
  onOpenChange: (open: boolean) => void;
}

export type FormValues = z.infer<typeof editInventoryAmountEventSchema>;

export function InventoryDetailsDialog({
  isOpen,
  eventId,
  stationId,
  onOpenChange,
  selectedInventory,
}: InventoryDetailsProps) {
  const {
    id: productId,
    size,
    totalAmount,
    name,
    nickname,
    category,
    styleNumber,
  } = selectedInventory;
  const queryClient = useQueryClient();

  const form = useForm<FormValues>({
    resolver: zodResolver(editInventoryAmountEventSchema),
    defaultValues: {
      amount: totalAmount,
    },
  });

  const updateProductAmountMutation = useApiMutation<{
    amount: number;
  }>(
    `/events/${eventId}/stations/${stationId}/products/${productId}/sizes/${size.id}`,
    {
      method: 'PATCH',
    },
    {
      onError: () => {
        toast.error('Uh oh! Something went wrong.');
      },
    }
  );

  const handleUpdateInventoryAmount = async ({ amount }: FormValues) => {
    try {
      await updateProductAmountMutation.mutateAsync({
        amount: amount,
      });
      queryClient.invalidateQueries({
        queryKey: ['eventProducts', eventId, stationId],
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Uh oh! Something went wrong.');
    }
  };

  return (
    <Dialog onOpenChange={onOpenChange} open={isOpen}>
      <DialogContent>
        <Form {...form}>
          <DialogHeader>
            <DialogTitle>Inventory Details</DialogTitle>
            <DialogDescription>Description</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Total Amount</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="Enter amount"
                      {...field}
                      min={0}
                      max={1000}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormItem>
              <FormLabel>Category</FormLabel>
              <FormControl>
                <Input value={category} disabled />
              </FormControl>
              <FormMessage />
            </FormItem>
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input value={name} disabled />
              </FormControl>
              <FormMessage />
            </FormItem>
            <FormItem>
              <FormLabel>Nickname</FormLabel>
              <FormControl>
                <Input value={nickname} disabled />
              </FormControl>
              <FormMessage />
            </FormItem>
            <FormItem>
              <FormLabel>Style Number</FormLabel>
              <FormControl>
                <Input value={styleNumber} disabled />
              </FormControl>
              <FormMessage />
            </FormItem>
            <FormItem>
              <FormLabel>Size</FormLabel>
              <FormControl>
                <Input value={size.size} disabled />
              </FormControl>
              <FormMessage />
            </FormItem>
          </div>

          <DialogFooter className="mt-6 gap-4">
            <DialogClose asChild>
              <Button variant="outline" data-testid="cancel-button">
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              data-testid="save-button"
              onClick={form.handleSubmit(handleUpdateInventoryAmount)}
              isLoading={updateProductAmountMutation.isPending}
            >
              Save
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
